import React, { useState, useEffect } from 'react';
import {
  Dialog,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { workflowService, ComplianceWorkflow, WorkflowCreateRequest } from '../services/workflowService';

interface WorkflowCreatorProps {
  open: boolean;
  onClose: () => void;
  onWorkflowCreated: (workflow: ComplianceWorkflow) => void;
}

const JURISDICTION_LABELS: Record<string, string> = {
  us_fda: 'US FDA',
  eu_mdr: 'EU MDR',
};

const WorkflowCreator: React.FC<WorkflowCreatorProps> = ({
  open,
  onClose,
  onWorkflowCreated,
}) => {
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [jurisdiction, setJurisdiction] = useState('');
  const [deviceClass, setDeviceClass] = useState('');
  const [deviceType, setDeviceType] = useState('');

  const [jurisdictions, setJurisdictions] = useState<string[]>([]);
  const [deviceClasses, setDeviceClasses] = useState<Array<{ value: string; display_name: string }>>([]);

  useEffect(() => {
    if (open) {
      workflowService.getJurisdictions().then((data) => {
        setJurisdictions(data.filter((j: string) => ['us_fda', 'eu_mdr'].includes(j)));
      }).catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (jurisdiction) {
      setDeviceClass('');
      workflowService.getDeviceClassesByJurisdiction(jurisdiction).then((res) => {
        setDeviceClasses(res.device_classes);
      }).catch(() => setDeviceClasses([]));
    } else {
      setDeviceClasses([]);
    }
  }, [jurisdiction]);

  const handleClose = () => {
    setName('');
    setJurisdiction('');
    setDeviceClass('');
    setDeviceType('');
    setError(null);
    setCreating(false);
    onClose();
  };

  const canCreate = name.trim() && jurisdiction && deviceClass && !creating;

  const handleCreate = async () => {
    if (!canCreate) return;
    setCreating(true);
    setError(null);
    try {
      const data: WorkflowCreateRequest = {
        name: name.trim(),
        jurisdiction,
        device_class: deviceClass,
        device_type: deviceType.trim(),
        device_category: '',
      };
      const workflow = await workflowService.createWorkflow(data);
      onWorkflowCreated(workflow);
      handleClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create workflow. Please try again.');
      setCreating(false);
    }
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      bgcolor: '#f8f9fb',
      fontSize: '0.875rem',
      transition: 'all 0.15s ease',
      '& fieldset': { borderColor: '#e5e7eb' },
      '&:hover': { bgcolor: '#f3f4f6', '& fieldset': { borderColor: '#d1d5db' } },
      '&.Mui-focused': { bgcolor: '#fff', '& fieldset': { borderColor: '#111827', borderWidth: '1.5px' } },
    },
    '& .MuiInputLabel-root': { fontSize: '0.8rem', color: '#6b7280' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#111827' },
  };

  const selectSx = {
    borderRadius: '10px',
    bgcolor: '#f8f9fb',
    fontSize: '0.875rem',
    '& fieldset': { borderColor: '#e5e7eb' },
    '&:hover': { bgcolor: '#f3f4f6', '& fieldset': { borderColor: '#d1d5db' } },
    '&.Mui-focused fieldset': { borderColor: '#111827', borderWidth: '1.5px' },
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 24px 48px -12px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          maxWidth: 480,
        },
      }}
    >
      {/* Header */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 3,
        pt: 2.5,
        pb: 0.5,
      }}>
        <Box>
          <Typography sx={{ fontSize: '1.1rem', fontWeight: 650, color: '#111827', letterSpacing: '-0.02em' }}>
            New Workflow
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', color: '#9ca3af', mt: 0.25 }}>
            Set up your compliance workflow
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: '#9ca3af',
            width: 32,
            height: 32,
            '&:hover': { color: '#111827', bgcolor: '#f3f4f6' },
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* Form */}
      <Box sx={{ px: 3, pt: 2, pb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          fullWidth
          label="Workflow name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Heart rate monitor — EU MDR"
          size="small"
          sx={inputSx}
        />

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
          <FormControl size="small" sx={{ '& .MuiInputLabel-root': { fontSize: '0.8rem', color: '#6b7280' }, '& .MuiInputLabel-root.Mui-focused': { color: '#111827' } }}>
            <InputLabel>Regulation</InputLabel>
            <Select
              value={jurisdiction}
              onChange={(e) => setJurisdiction(e.target.value)}
              label="Regulation"
              sx={selectSx}
            >
              {jurisdictions.map((j) => (
                <MenuItem key={j} value={j} sx={{ fontSize: '0.85rem' }}>
                  {JURISDICTION_LABELS[j] || j}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ '& .MuiInputLabel-root': { fontSize: '0.8rem', color: '#6b7280' }, '& .MuiInputLabel-root.Mui-focused': { color: '#111827' } }}>
            <InputLabel>Device class</InputLabel>
            <Select
              value={deviceClass}
              onChange={(e) => setDeviceClass(e.target.value)}
              label="Device class"
              disabled={!jurisdiction}
              sx={selectSx}
            >
              {deviceClasses.map((dc) => (
                <MenuItem key={dc.value} value={dc.value} sx={{ fontSize: '0.85rem' }}>
                  {dc.display_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <TextField
          fullWidth
          label="Device type"
          value={deviceType}
          onChange={(e) => setDeviceType(e.target.value)}
          placeholder="e.g. Cardiac monitor, Pulse oximeter"
          size="small"
          sx={inputSx}
        />

        {/* Error */}
        {error && (
          <Typography sx={{ fontSize: '0.78rem', color: '#ef4444', bgcolor: '#fef2f2', px: 1.5, py: 1, borderRadius: '8px' }}>
            {error}
          </Typography>
        )}

        {/* Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 0.5 }}>
          <Button
            onClick={handleClose}
            disabled={creating}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.82rem',
              color: '#6b7280',
              borderRadius: '10px',
              px: 2.5,
              '&:hover': { bgcolor: '#f3f4f6', color: '#374151' },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!canCreate}
            disableElevation
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.82rem',
              bgcolor: '#111827',
              color: '#fff',
              borderRadius: '10px',
              px: 3,
              py: 0.9,
              letterSpacing: '-0.01em',
              transition: 'all 0.15s ease',
              '&:hover': { bgcolor: '#1f2937' },
              '&:disabled': { bgcolor: '#e5e7eb', color: '#9ca3af' },
            }}
          >
            {creating ? (
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: '5px',
                '& span': {
                  width: 5, height: 5, borderRadius: '50%', bgcolor: '#fff',
                  display: 'inline-block',
                  animation: 'createPulse 1.4s ease-in-out infinite',
                },
                '& span:nth-of-type(2)': { animationDelay: '0.2s' },
                '& span:nth-of-type(3)': { animationDelay: '0.4s' },
                '@keyframes createPulse': {
                  '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                  '40%': { opacity: 1, transform: 'scale(1)' },
                },
              }}>
                <span /><span /><span />
              </Box>
            ) : 'Create workflow'}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default WorkflowCreator;
